import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Step, StepLabel, Stepper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import FileUploader from "../../controls/FileUploader";
import UploadStatusIndicator from "../../controls/UploadStatusIndicator";
import { questionBankService } from "../../../services/questionBankService";
import { useFileUpload } from "../../../hooks/useFileUpload";
import { UtilityHelper } from "../../../utils/UtilityHelper";
import { Constants } from "../../../models/Constants";
import parse from "html-react-parser";

/** HealthFacilities component to generate/upload and view template*/
const HealthFacilities = () => {
  const { t } = useTranslation();
  document.title = t("translation:app.HealthFacilitiesPageTitle");
  const [isFileSizeMaximum, setIsFileSizeMaximum] = useState<boolean>(false);
  const [isValidFileExtension, setIsValidFileExtension] =
    useState<boolean>(false);
  let selectedCountryId = sessionStorage.getItem(
    Constants.SessionStorageKey.SELECTED_COUNTRY
  );
  const [fileName, setfileName] = useState<string>();
  const { status: uploadStatus, error: uploadError, execute: executeUpload, reset: resetUpload } = useFileUpload();
  const [activeStepIndex, setActiveStepIndex] = useState<number>(-1);
  const [currentSteps, setCurrentSteps] = useState<string[]>([]);
  const [fileInputResetKey, setFileInputResetKey] = useState<number>(0);

  const uploadSteps = [
    `${t("Common.Uploading")}...`,
    t("Common.UploadStepParsingExcel"),
    t("Common.UploadStepCalculating"),
    t("Common.UploadStepSaving"),
  ];
  const exportSteps = [
    `${t("Common.Exporting")}...`,
    t("Common.ExportStepFetching"),
    t("Common.ExportStepGenerating"),
  ];

  const handleProgress = (step: string) => {
    const idx = uploadSteps.indexOf(step);
    setActiveStepIndex(idx >= 0 ? idx : 0);
  };
  const handleAllComplete = () => {
    setActiveStepIndex(uploadSteps.length);
    GetLastUploadedFileDetails();
  };
  const handleExportProgress = (step: string) => {
    const idx = exportSteps.indexOf(step);
    setActiveStepIndex(idx >= 0 ? idx : 0);
  };
  const handleExportComplete = () => setActiveStepIndex(exportSteps.length);

  useEffect(() => {
    GetLastUploadedFileDetails();
  }, []);

  // call on page load set file name
  const GetLastUploadedFileDetails = () => {
    questionBankService
      .getLastUploadedFileName(selectedCountryId ?? "")
      .then((record: string) => {
        setfileName(record);
      });
  };

  // Triggers whenever user clicks on upload button
  const uploadFile = (evt: React.ChangeEvent<HTMLInputElement>) => {
    // check file size
    const filesize = evt.target.files ? evt.target.files[0].size : 0;
    if (filesize > Constants.Common.MaxFileSize) {
      //100MB
      setIsFileSizeMaximum(true);
      return;
    } else {
      setIsFileSizeMaximum(false);
    }

    // check file extension
    const fileExtension: string | undefined = evt.target.files
      ? evt.target.files[0].name.split(".").pop()
      : "";
    if (
      Constants.Common.ValidExcelExtensions.indexOf(fileExtension as string) ===
      -1
    ) {
      setIsValidFileExtension(true);
      return;
    } else {
      setIsValidFileExtension(false);
    }

    // FormData object to pass along with file so it can be mapped at server side
    const formData = new FormData();
    formData.append("File", evt.target.files ? evt.target.files[0] : "");
    formData.append("CountryId", selectedCountryId ?? "");

    setCurrentSteps(uploadSteps);
    setActiveStepIndex(0);
    executeUpload(() => questionBankService.uploadHealthFacilityTemplateResponse(formData, handleProgress, handleAllComplete))
      .then(() => {
        setActiveStepIndex(-1);
        setFileInputResetKey(k => k + 1);
        resetUpload();
      })
      .catch(() => {
        setActiveStepIndex(-1);
        resetUpload();
      });
  };

  // Triggers whenever user clicks on Generate Template button
  const onGenerateTemplate = () => {
    const fileName: string =
      t("Assessment.HealthFacilities.HealthFacilityFileName") + ".xlsx";

    questionBankService.downloadHealthFacilities().then((content: any) => {
      UtilityHelper.download(content, fileName);
    });
  };

  // Triggers whenever user clicks on export/ view existing health facility button
  const exportExcel = () => {
    const downloadFileName: string =
      t("Assessment.HealthFacilities.HealthFacilityFileName") + ".xlsx";
    setCurrentSteps(exportSteps);
    setActiveStepIndex(0);
    questionBankService
      .exportHealthFacilityExcelAsync(selectedCountryId ?? "", handleExportProgress, handleExportComplete)
      .then((blob: Blob) => {
        setActiveStepIndex(-1);
        UtilityHelper.download(blob, downloadFileName);
      })
      .catch(() => {
        setActiveStepIndex(-1);
      });
  };

  return (
    <section className="page-full-section">
      <div className="container-fluid">
        <div className="row d-flex document-title-section">
          <div
            className="col-md-4
                        d-flex
                        justify-content-left
                        align-items-center"
          >
            <span className="fw-bold text-uppercase heading-title">
              {t("Assessment.HealthFacilities.HealthFacilitiesHeading")}
            </span>
          </div>
        </div>

        <div>
          <p>{parse(t("Assessment.HealthFacilities.HealthFacilitiesDesc"))}</p>
        </div>

        <div className="response-action-wrapper h-400 d-flex align-items-center justify-content-center">
          <div className="d-flex flex-column align-items-center w-100">
            <div className="button-action-section d-flex justify-content-center p-3">
              <Button
                className={classNames("btn", "app-btn-secondary")}
                onClick={exportExcel}
              >
                {t("Assessment.HealthFacilities.ViewHealthFacility")}
              </Button>

              <Button
                className={classNames("btn", "app-btn-secondary")}
                onClick={onGenerateTemplate}
              >
                {t("Assessment.HealthFacilities.GenerateTemplate")}
              </Button>

              <FileUploader
                key={fileInputResetKey}
                id="template"
                linkCss={classNames("btn", "app-btn-secondary", "ms-2", "upload-btn")}
                onChange={uploadFile}
                accept=".xlsx, .xls"
              >
                <div style={{ display: "grid" }}>
                  {fileName && (
                    <>
                      <p className="m-0">
                        {" "}
                        <i className="d-inline-flex mx-2 align-items-center">
                          {t("Assessment.HealthFacilities.FileUploadSize")}
                        </i>
                      </p>

                      <p className="m-0">
                        <span className="d-inline-flex mx-2 align-items-center">
                          <b className="me-2">{t("Common.LastUploadedFile")}:</b>{" "}
                          {fileName}
                        </span>
                      </p>
                    </>
                  )}
                  {!fileName && (
                    <>
                      <i className="d-inline-flex mx-2 align-items-center">
                        {t(
                          "Assessment.DataCollection.DataQualityAssessment.FileUploadSize"
                        )}
                      </i>
                    </>
                  )}
                </div>
              </FileUploader>
            </div>

            {activeStepIndex < 0 && <UploadStatusIndicator status={uploadStatus} error={uploadError} />}

            <Dialog open={activeStepIndex >= 0} disableEscapeKeyDown>
              <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography>{currentSteps[activeStepIndex] ?? `${t("Common.Uploading")}...`}</Typography>
                </Box>
              </DialogTitle>
              <DialogContent>
                <Stepper activeStep={activeStepIndex} alternativeLabel sx={{ minWidth: 480 }}>
                  {currentSteps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <>
          {isFileSizeMaximum && (
            <span className="d-flex justify-content-center Mui-error">
              {" "}
              {t("Exception.InvalidFileSize")}{" "}
            </span>
          )}
          {isValidFileExtension && (
            <span className="d-flex justify-content-center Mui-error">
              {" "}
              {t("Exception.InvalidFile")}{" "}
            </span>
          )}
        </>
      </div>
    </section>
  );
};

export default HealthFacilities;
